import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { KeyboardEvent as ReactKeyboardEvent } from 'react';

export interface GridMenuItem {
  id: string;
  label: string;
  hint?: string;
  disabled?: boolean;
  separatorBefore?: boolean;
  run(): void;
}

/** Context menu of the grid (right click, Shift+F10, ContextMenu key). ↑/↓, Enter, Escape. */
export function GridMenu({
  x,
  y,
  label,
  items,
  onClose,
}: {
  x: number;
  y: number;
  label: string;
  items: readonly GridMenuItem[];
  onClose(): void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ left: x, top: y });
  // Keep the menu inside the viewport (opened near the bottom pane's lower edge it would overflow).
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const left = Math.max(4, Math.min(x, window.innerWidth - r.width - 4));
    const top = Math.max(4, Math.min(y, window.innerHeight - r.height - 4));
    setPos((p) => (p.left === left && p.top === top ? p : { left, top }));
  }, [x, y]);
  useEffect(() => {
    ref.current
      ?.querySelector<HTMLElement>('[role="menuitem"]:not([aria-disabled="true"])')
      ?.focus();
  }, []);
  useEffect(() => {
    const onDown = (e: PointerEvent) => {
      if (!ref.current?.contains(e.target as Node)) onClose();
    };
    document.addEventListener('pointerdown', onDown);
    return () => document.removeEventListener('pointerdown', onDown);
  }, [onClose]);

  const onKeyDown = (e: ReactKeyboardEvent) => {
    e.stopPropagation();
    const els = [
      ...(ref.current?.querySelectorAll<HTMLElement>(
        '[role="menuitem"]:not([aria-disabled="true"])',
      ) ?? []),
    ];
    const idx = els.indexOf(document.activeElement as HTMLElement);
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      els[(idx + (e.key === 'ArrowDown' ? 1 : -1) + els.length) % els.length]?.focus();
    } else if (e.key === 'Home' || e.key === 'End') {
      e.preventDefault();
      els[e.key === 'Home' ? 0 : els.length - 1]?.focus();
    } else if (e.key === 'Escape' || e.key === 'Tab') {
      e.preventDefault();
      onClose();
    }
  };

  return (
    <div
      ref={ref}
      role="menu"
      aria-label={label}
      className="context-menu dg-menu"
      style={pos}
      tabIndex={-1}
      onKeyDown={onKeyDown}
    >
      {items.map((it) => (
        <div
          key={it.id}
          role="menuitem"
          tabIndex={-1}
          aria-disabled={it.disabled ? true : undefined}
          className={`menu-item${it.separatorBefore ? ' sep' : ''}`}
          onClick={() => {
            if (it.disabled) return;
            onClose();
            it.run();
          }}
          onKeyDown={(e) => {
            if ((e.key === 'Enter' || e.key === ' ') && !it.disabled) {
              e.preventDefault();
              e.stopPropagation();
              onClose();
              it.run();
            }
          }}
        >
          <span>{it.label}</span>
          {it.hint && <kbd>{it.hint}</kbd>}
        </div>
      ))}
    </div>
  );
}
